import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Zap, Users, TrendingUp, Sparkles, ArrowRight,
  Home as HomeIcon, Briefcase, Car, Building2, Truck, IndianRupee, HeartHandshake,
} from "lucide-react";
import { LOAN_PRODUCTS, WHATSAPP_NUMBER } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/8867420/pexels-photo-8867420.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ABOUT_IMG = "https://images.pexels.com/photos/8068656/pexels-photo-8068656.jpeg?auto=compress&cs=tinysrgb&w=1400";

const iconFor = (slug) => {
  if (slug.includes("home")) return HomeIcon;
  if (slug.includes("business") || slug.includes("msme")) return Briefcase;
  if (slug.includes("car")) return Car;
  if (slug.includes("commercial")) return Truck;
  if (slug.includes("mortgage") || slug.includes("property")) return Building2;
  return IndianRupee;
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-[#06152D]"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* 45% dark overlay for text legibility */}
        <div className="absolute inset-0 bg-[#06152D]/45" aria-hidden="true" />
        {/* Left-side wash keeps right side of photo visible */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{ background: "linear-gradient(90deg, rgba(6,21,45,0.55) 0%, rgba(6,21,45,0.25) 45%, rgba(6,21,45,0) 80%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 text-xs font-medium text-[#EBF2FF]">
              <Sparkles size={12} /> Trusted Loan Advisor Across Telangana
            </div>
            <h1 className="mt-6 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="hero-headline">
              Your Trusted<br />
              <span className="text-[#8AB4FF]">Loan &amp; Insurance</span> Partner
            </h1>
            <p className="mt-6 text-lg text-[#D6E3FA] leading-relaxed max-w-xl" data-testid="hero-subheading">
              Personal, Business, Home, Mortgage, MSME, Vehicle &amp; Insurance solutions.
              We help customers across Telangana connect with leading Banks &amp; NBFCs through a transparent and professional process.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/apply"
                className="bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 lj-focus"
                data-testid="hero-apply-cta"
              >
                Apply Online <ArrowRight size={16} />
              </Link>
              <Link
                to="/emi-calculator"
                className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 backdrop-blur lj-focus"
                data-testid="hero-emi-cta"
              >
                EMI Calculator
              </Link>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#EBF2FF] hover:text-white font-medium px-4 py-3.5 inline-flex items-center gap-2"
                data-testid="hero-whatsapp"
              >
                Chat on WhatsApp →
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["500+", "Happy customers"],
                ["9", "Loan products"],
                ["24 hrs", "Response time"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-extrabold text-white">{n}</div>
                  <div className="text-xs text-[#B5C4E1] mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">Our Loan Products</div>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#06152D]">
                Nine loan products. One trusted partner.
              </h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-[#0052FF] hover:text-[#003FCC]" data-testid="products-view-all">
              View all services →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LOAN_PRODUCTS.map((p) => {
              const Icon = iconFor(p.slug);
              return (
                <Link
                  key={p.slug}
                  to={`/services/${p.slug}`}
                  className="lj-card-lift bg-white border border-[#E4E4E7] rounded-2xl p-6 block group"
                  data-testid={`home-product-${p.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#0052FF]">
                      <Icon size={22} />
                    </div>
                    <div className="text-[10px] font-semibold text-[#059669] text-right leading-tight max-w-[110px]">As per Bank/NBFC<br/>Eligibility</div>
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg text-[#06152D]">{p.label}</h3>
                  <p className="mt-2 text-sm text-[#52525B] leading-relaxed">{p.tagline}</p>
                  <div className="mt-4 pt-4 border-t border-[#E4E4E7] flex items-center justify-between text-xs text-[#52525B]">
                    <span>{p.tenure}</span>
                    <span className="text-[#0052FF] font-semibold group-hover:translate-x-1 transition-transform">Learn more →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">Why Loan Junction</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#06152D]">
              Statewide reach. Enterprise-grade process.
            </h2>
            <p className="mt-4 text-[#52525B] leading-relaxed">
              We proudly serve customers across Telangana with professional loan and insurance
              consultancy services — backed by a rigorous, banking-grade CRM that tracks every
              file so nothing slips through the cracks.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {[
                { icon: Zap, title: "Fast approval", body: "24-hour response, disbursals in 3–7 days for most products." },
                { icon: ShieldCheck, title: "Zero hidden charges", body: "All fees disclosed upfront in your sanction letter." },
                { icon: Users, title: "Dedicated advisor", body: "One point of contact from application to disbursal." },
                { icon: HeartHandshake, title: "Post-loan support", body: "EMI reminders, part-pre payment help, insurance renewals." },
              ].map((f) => (
                <div key={f.title} className="flex gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border border-[#E4E4E7] flex items-center justify-center text-[#0052FF] shrink-0">
                    <f.icon size={18} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#06152D]">{f.title}</div>
                    <div className="text-sm text-[#52525B] mt-0.5">{f.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src={ABOUT_IMG}
              alt="Loan Junction advisors"
              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white border border-[#E4E4E7] rounded-2xl p-5 shadow-lg hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#059669]/10 flex items-center justify-center text-[#059669]">
                  <TrendingUp size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-2xl text-[#06152D]">₹42+ Cr</div>
                  <div className="text-xs text-[#52525B]">Disbursed to Telangana customers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#06152D] text-white lj-grain">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Ready to talk to a real advisor?
          </h2>
          <p className="mt-4 text-[#B5C4E1] max-w-xl mx-auto">
            Submit your details online, or WhatsApp us — either way you'll hear from a Loan Junction advisor within a business day.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/apply" className="bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-6 py-3.5 rounded-full" data-testid="cta-apply">
              Apply Online
            </Link>
            <Link to="/contact" className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full backdrop-blur" data-testid="cta-contact">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
